import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/db";
import { useAuth } from "@/lib/auth-context";
import { Guard } from "@/components/Guard";
import { PageHeader } from "@/components/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Loader2, Upload, FileImage, Building, Wallet, Tag, Activity, BarChart3, Receipt, Pencil, Check, X, FileDown, Archive, Shapes, Landmark, RefreshCw, ChevronDown, ChevronRight, Users, ArchiveRestore } from "lucide-react";
import { api } from "@/lib/api";
import { fetchMemberCategories, memberCategoriesQueryKey, type MemberCategoryRow } from "@/lib/member-categories";
import { toast } from "sonner";
import { format } from "date-fns";
import { ExpenseReportsTab } from "@/components/ExpenseReportsTab";
import { syncHelloAssoPayouts, fetchHelloAssoPaymentItems, type HelloAssoPaymentItem } from "@/lib/helloasso-sync";
// jsPDF + autoTable (~143 KB Brotli) chargés à la demande lors de l'export PDF
// pour ne pas alourdir le bundle initial (notamment /login).

type NewParam = "expense" | "income" | "reimbursement" | "expense-report" | undefined;

export const Route = createFileRoute("/treasury")({
  validateSearch: (s: Record<string, unknown>): { new?: NewParam; tab?: string } => {
    const v = s.new;
    const out: { new?: NewParam; tab?: string } = {};
    if (v === "expense" || v === "income" || v === "reimbursement" || v === "expense-report") out.new = v;
    if (typeof s.tab === "string") out.tab = s.tab;
    return out;
  },
  component: () => <Guard module="treasury"><TreasuryPage /></Guard>,
});

// Legacy fallback labels (utilisés si un type n'est pas trouvé dans la table dynamique)
const LEGACY_LABELS: Record<string, string> = {
  income: "Recette", expense: "Dépense", reimbursement: "Remboursement", bank_movement: "Mouvement bancaire",
};
const SIGN_COLORS: Record<string, string> = {
  positive: "bg-success/15 text-success",
  negative: "bg-destructive/15 text-destructive",
  neutral:  "bg-primary/15 text-primary",
};

type TxKind = { id: string; slug: string; label: string; sign: "positive" | "negative" | "neutral"; is_system: boolean; archived: boolean; sort_order: number };

function useTransactionKinds() {
  return useQuery({
    queryKey: ["transaction_kinds"],
    queryFn: async () => {
      const { data, error } = await supabase.from("transaction_kinds" as any).select("*").order("sort_order");
      if (error) throw error;
      return (data ?? []) as unknown as TxKind[];
    },
  });
}

function TreasuryPage() {
  const routeSearch = Route.useSearch();
  const navigate = useNavigate();
  const [tab, setTab] = useState<string>(routeSearch.tab ?? "transactions");
  const [submitExpenseOpen, setSubmitExpenseOpen] = useState(false);

  useEffect(() => {
    if (routeSearch.new === "expense-report") {
      setTab("expense-reports");
      setSubmitExpenseOpen(true);
      navigate({ to: "/treasury", search: {}, replace: true });
    }
  }, [routeSearch.new, navigate]);

  return (
    <>
      <PageHeader title="Trésorerie" description="Recettes, dépenses, remboursements, budgets et mouvements bancaires" />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="transactions"><Wallet className="h-4 w-4 mr-1.5" />Mouvements</TabsTrigger>
          <TabsTrigger value="helloasso-payouts"><Landmark className="h-4 w-4 mr-1.5" />Versements HelloAsso</TabsTrigger>
          <TabsTrigger value="expense-reports"><Receipt className="h-4 w-4 mr-1.5" />Notes de frais</TabsTrigger>
          <TabsTrigger value="budget"><BarChart3 className="h-4 w-4 mr-1.5" />Budget annuel</TabsTrigger>
          <TabsTrigger value="bank"><Building className="h-4 w-4 mr-1.5" />Banque</TabsTrigger>
          <TabsTrigger value="categories"><Tag className="h-4 w-4 mr-1.5" />Catégories</TabsTrigger>
          <TabsTrigger value="member-categories"><Users className="h-4 w-4 mr-1.5" />Catégories d'adhérents</TabsTrigger>
          <TabsTrigger value="kinds"><Shapes className="h-4 w-4 mr-1.5" />Types</TabsTrigger>
          <TabsTrigger value="activities"><Activity className="h-4 w-4 mr-1.5" />Activités</TabsTrigger>
        </TabsList>
        <TabsContent value="transactions" className="mt-4"><TransactionsTab /></TabsContent>
        <TabsContent value="helloasso-payouts" className="mt-4"><HelloAssoPayoutsTab /></TabsContent>
        <TabsContent value="expense-reports" className="mt-4">
          <ExpenseReportsTab openSubmit={submitExpenseOpen} setOpenSubmit={setSubmitExpenseOpen} />
        </TabsContent>
        <TabsContent value="budget" className="mt-4"><BudgetTab /></TabsContent>
        <TabsContent value="bank" className="mt-4"><BankTab /></TabsContent>
        <TabsContent value="categories" className="mt-4"><CategoriesTab /></TabsContent>
        <TabsContent value="member-categories" className="mt-4"><MemberCategoriesTab /></TabsContent>
        <TabsContent value="kinds" className="mt-4"><KindsTab /></TabsContent>
        <TabsContent value="activities" className="mt-4"><ActivitiesTab /></TabsContent>
      </Tabs>
    </>
  );
}

const MONTHS = [
  { v: "all", l: "Tous les mois" },
  { v: "01", l: "Janvier" }, { v: "02", l: "Février" }, { v: "03", l: "Mars" },
  { v: "04", l: "Avril" }, { v: "05", l: "Mai" }, { v: "06", l: "Juin" },
  { v: "07", l: "Juillet" }, { v: "08", l: "Août" }, { v: "09", l: "Septembre" },
  { v: "10", l: "Octobre" }, { v: "11", l: "Novembre" }, { v: "12", l: "Décembre" },
];

function TransactionsTab() {
  const { hasAccess } = useAuth();
  const canWrite = hasAccess("treasury", "write");
  const canAdmin = hasAccess("treasury", "admin");
  const qc = useQueryClient();
  const routeSearch = Route.useSearch();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [initialKind, setInitialKind] = useState<NewParam>(undefined);
  const [filter, setFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState<string>(String(currentYear));
  const [month, setMonth] = useState<string>("all");
  const [showArchived, setShowArchived] = useState(false);
  const [hideZero, setHideZero] = useState(false);
  const [archiveYearOpen, setArchiveYearOpen] = useState(false);

  useEffect(() => {
    const v = routeSearch.new;
    if ((v === "expense" || v === "income" || v === "reimbursement") && canWrite) {
      setInitialKind(v);
      setOpen(true);
      navigate({ to: "/treasury", search: {}, replace: true });
    }
  }, [routeSearch.new, canWrite, navigate]);

  const { data: kinds = [] } = useTransactionKinds();

  const { data: txs = [] } = useQuery({
    queryKey: ["transactions", year, month, showArchived],
    queryFn: async () => {
      let q = supabase.from("transactions")
        .select("*, treasury_categories(name,accounting_code), members(first_name,last_name), activities(name)")
        .order("date", { ascending: false }).limit(1000);
      if (!showArchived) q = q.eq("archived_year", false);
      if (year !== "all") {
        const start = `${year}-${month === "all" ? "01" : month}-01`;
        let end: string;
        if (month === "all") end = `${year}-12-31`;
        else {
          const y = Number(year), m = Number(month);
          const last = new Date(y, m, 0).getDate();
          end = `${year}-${month}-${String(last).padStart(2, "0")}`;
        }
        q = q.gte("date", start).lte("date", end);
      }
      const { data, error } = await q;
      if (error) throw error;
      return data as any[];
    },
  });

  // Liste d'années dispo (à partir des dates existantes + année courante)
  const { data: yearsList = [] } = useQuery({
    queryKey: ["transactions-years"],
    queryFn: async () => {
      const { data } = await supabase.from("transactions").select("date").order("date", { ascending: false }).limit(2000);
      const set = new Set<string>([String(currentYear)]);
      (data ?? []).forEach((r: any) => { if (r.date) set.add(String(r.date).slice(0, 4)); });
      return Array.from(set).sort((a, b) => Number(b) - Number(a));
    },
  });

  const { data: catsList = [] } = useQuery({
    queryKey: ["treasury_categories_filter"],
    queryFn: async () => (await supabase.from("treasury_categories").select("id,name,kind,accounting_code").order("name")).data ?? [],
  });

  const visible = txs
    .filter((t) => filter === "all" || t.kind === filter)
    .filter((t) => t.kind !== "bank_movement")
    .filter((t) => !hideZero || Number(t.amount) !== 0)
    .filter((t) => categoryFilter === "all" || t.category_id === categoryFilter);

  const totals = useMemo(() => {
    let inc = 0, exp = 0;
    for (const t of visible) {
      const amt = Number(t.amount);
      if (t.kind === "income") inc += amt; else exp += amt;
    }
    return { inc, exp, balance: inc - exp };
  }, [visible]);

  const kindLabel = (slug: string) => kinds.find(k => k.slug === slug)?.label ?? LEGACY_LABELS[slug] ?? slug;
  const kindSign = (slug: string): "positive" | "negative" | "neutral" => {
    const k = kinds.find(x => x.slug === slug);
    if (k) return k.sign;
    if (slug === "income") return "positive";
    if (slug === "bank_movement") return "neutral";
    return "negative";
  };

  const del = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("transactions").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["transactions"] }); qc.invalidateQueries({ queryKey: ["dashboard-stats"] }); toast.success("Supprimé"); },
  });

  const archiveYear = useMutation({
    mutationFn: async (y: string) => {
      const { error } = await supabase.from("transactions")
        .update({ archived_year: true })
        .gte("date", `${y}-01-01`).lte("date", `${y}-12-31`);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["transactions"] }); toast.success("Année archivée"); setArchiveYearOpen(false); },
    onError: (e: any) => toast.error(e.message),
  });

  const unarchiveYear = useMutation({
    mutationFn: async (y: string) => {
      const { error } = await supabase.from("transactions")
        .update({ archived_year: false })
        .gte("date", `${y}-01-01`).lte("date", `${y}-12-31`);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["transactions"] }); toast.success("Année désarchivée"); },
    onError: (e: any) => toast.error(e.message),
  });

  const { data: orgSettings } = useQuery({
    queryKey: ["org_settings"],
    queryFn: async () => (await supabase.from("org_settings").select("name, logo_url").limit(1).maybeSingle()).data,
  });

  const loadLogoDataUrl = async (url: string): Promise<{ dataUrl: string; w: number; h: number; format: string } | null> => {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      const dims = await new Promise<{ w: number; h: number }>((resolve) => {
        const img = new Image();
        img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
        img.onerror = () => resolve({ w: 0, h: 0 });
        img.src = dataUrl;
      });
      const format = blob.type.includes("png") ? "PNG" : (blob.type.includes("jpeg") || blob.type.includes("jpg")) ? "JPEG" : "PNG";
      return { dataUrl, w: dims.w, h: dims.h, format };
    } catch { return null; }
  };

  const exportPdf = async () => {
    const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([
      import("jspdf"),
      import("jspdf-autotable"),
    ]);
    const doc = new jsPDF({ orientation: "landscape" });
    const pageW = doc.internal.pageSize.getWidth();

    const periodLabel = year === "all"
      ? "Toutes périodes"
      : `${month === "all" ? "Année" : MONTHS.find(m => m.v === month)?.l} ${year}`;

    const orgName = orgSettings?.name?.trim() || "Liste des mouvements";
    let textX = 14;

    if (orgSettings?.logo_url) {
      const logo = await loadLogoDataUrl(orgSettings.logo_url);
      if (logo && logo.w > 0) {
        const maxH = 18;
        const ratio = logo.w / logo.h;
        const w = maxH * ratio;
        try {
          doc.addImage(logo.dataUrl, logo.format, 14, 10, w, maxH);
          textX = 14 + w + 6;
        } catch { /* ignore */ }
      }
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(20);
    doc.text(orgName, textX, 18);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(13);
    doc.setTextColor(90);
    doc.text("Liste des mouvements", textX, 26);

    doc.setFontSize(10);
    doc.setTextColor(110);
    const rightX = pageW - 14;
    doc.text(`Période : ${periodLabel}`, rightX, 14, { align: "right" });
    doc.text(`Filtre : ${filter === "all" ? "Tous types" : kindLabel(filter)}`, rightX, 20, { align: "right" });
    doc.text(`Édité le ${format(new Date(), "dd/MM/yyyy HH:mm")}`, rightX, 26, { align: "right" });

    doc.setDrawColor(180);
    doc.setLineWidth(0.4);
    doc.line(14, 33, pageW - 14, 33);

    autoTable(doc, {
      startY: 40,
      head: [["Date", "Type", "Catégorie", "Activité", "Description", "Dépenses (€)", "Recettes (€)"]],
      body: visible.map((t) => {
        const isIncome = t.kind === "income";
        const amt = Number(t.amount).toFixed(2);
        return [
          t.date,
          kindLabel(t.kind),
          [t.treasury_categories?.name, t.treasury_categories?.accounting_code ? `(${t.treasury_categories.accounting_code})` : ""].filter(Boolean).join(" ").replace(/\s+/g, " ").trim(),
          t.activities?.name ?? "—",
          [t.description?.replace(/\s+/g, " ").trim(), t.members ? `${t.members.first_name} ${t.members.last_name}` : ""].filter(Boolean).join("\n"),
          isIncome ? "" : amt,
          isIncome ? amt : "",
        ];
      }),
      foot: [
        [
          { content: "Totaux", colSpan: 5, styles: { halign: "right", fontStyle: "bold", fontSize: 11, fillColor: [226, 232, 240] as [number, number, number] } },
          { content: totals.exp.toFixed(2), styles: { halign: "right", fontStyle: "bold", fontSize: 11, textColor: [185, 28, 28] as [number, number, number], fillColor: [254, 226, 226] as [number, number, number] } },
          { content: totals.inc.toFixed(2), styles: { halign: "right", fontStyle: "bold", fontSize: 11, textColor: [21, 128, 61] as [number, number, number], fillColor: [220, 252, 231] as [number, number, number] } },
        ],
        [
          { content: "Solde", colSpan: 5, styles: { halign: "right", fontStyle: "bold", fontSize: 12, fillColor: [241, 245, 249] as [number, number, number] } },
          {
            content: `${totals.balance >= 0 ? "+" : ""}${totals.balance.toFixed(2)} €`,
            colSpan: 2,
            styles: {
              halign: "right",
              fontStyle: "bold",
              fontSize: 12,
              textColor: (totals.balance >= 0 ? [21, 128, 61] : [185, 28, 28]) as [number, number, number],
              fillColor: (totals.balance >= 0 ? [220, 252, 231] : [254, 226, 226]) as [number, number, number],
            },
          },
        ],
      ],
      styles: {
        font: "helvetica",
        fontSize: 10,
        cellPadding: 4,
        textColor: 30,
        lineColor: [220, 220, 220],
        lineWidth: 0.1,
        valign: "middle",
      },
      headStyles: {
        fillColor: [30, 41, 59],
        textColor: 255,
        fontStyle: "bold",
        fontSize: 10,
        cellPadding: 5,
      },
      bodyStyles: { fontSize: 9 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      footStyles: {
        fillColor: [226, 232, 240],
        textColor: 15,
        fontStyle: "bold",
        cellPadding: 5,
      },
      columnStyles: {
        0: { cellWidth: 26, halign: "left" },
        1: { cellWidth: 28 },
        2: { cellWidth: 42 },
        3: { cellWidth: 30 },
        4: { cellWidth: "auto", overflow: "linebreak", halign: "left" },
        5: { halign: "right", cellWidth: 26, fontStyle: "bold", textColor: [185, 28, 28] },
        6: { halign: "right", cellWidth: 26, fontStyle: "bold", textColor: [21, 128, 61] },
      },
      margin: { left: 14, right: 14 },
    });

    // Pied de page : numéro de page
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(140);
      doc.text(`Page ${i} / ${pageCount}`, pageW - 14, doc.internal.pageSize.getHeight() - 8, { align: "right" });
    }

    doc.save(`mouvements-${year}${month !== "all" ? `-${month}` : ""}.pdf`);
  };

  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <Label className="text-xs">Type</Label>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                <SelectItem value="income">Recettes</SelectItem>
                <SelectItem value="expense">Dépenses</SelectItem>
                <SelectItem value="reimbursement">Remboursements</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Année</Label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="w-[120px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                {yearsList.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Mois</Label>
            <Select value={month} onValueChange={setMonth} disabled={year === "all"}>
              <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTHS.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Catégorie</Label>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[220px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les catégories</SelectItem>
                {(catsList as any[])
                  .filter((c) => filter === "all" || c.kind === filter)
                  .map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}{c.accounting_code ? ` (${c.accounting_code})` : ""}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 pb-2">
            <Switch id="archived" checked={showArchived} onCheckedChange={setShowArchived} />
            <Label htmlFor="archived" className="text-xs cursor-pointer">Inclure archives</Label>
          </div>
          <div className="flex items-center gap-2 pb-2">
            <Switch id="hide-zero" checked={hideZero} onCheckedChange={setHideZero} />
            <Label htmlFor="hide-zero" className="text-xs cursor-pointer">Masquer montants nuls</Label>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={exportPdf}><FileDown className="h-4 w-4 mr-2" />Export PDF</Button>
          {canAdmin && <Button variant="outline" size="sm" onClick={() => setArchiveYearOpen(true)}><Archive className="h-4 w-4 mr-2" />Archiver une année</Button>}
          {canWrite && <Button size="sm" onClick={() => { setInitialKind("expense"); setOpen(true); }}><Plus className="h-4 w-4 mr-2" />Nouveau mouvement</Button>}
        </div>
      </div>

      {/* Bandeau totaux */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <Card className="p-3"><div className="text-xs text-muted-foreground">Recettes</div><div className="text-lg font-mono text-success">+{totals.inc.toFixed(2)} €</div></Card>
        <Card className="p-3"><div className="text-xs text-muted-foreground">Dépenses</div><div className="text-lg font-mono text-destructive">-{totals.exp.toFixed(2)} €</div></Card>
        <Card className="p-3"><div className="text-xs text-muted-foreground">Solde</div><div className={`text-lg font-mono ${totals.balance >= 0 ? "text-success" : "text-destructive"}`}>{totals.balance >= 0 ? "+" : ""}{totals.balance.toFixed(2)} €</div></Card>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8"></TableHead>
              <TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Catégorie</TableHead>
              <TableHead>Activité</TableHead>
              <TableHead>Description</TableHead><TableHead>Justif.</TableHead>
              <TableHead className="text-right">Montant</TableHead><TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">Aucun mouvement</TableCell></TableRow>}
            {visible.map((t) => (
              <TreasuryTxRow
                key={t.id}
                t={t}
                sign={kindSign(t.kind)}
                kindLabel={kindLabel(t.kind)}
                canAdmin={canAdmin}
                onDelete={(id) => { if (confirm("Supprimer ?")) del.mutate(id); }}
              />
            ))}
          </TableBody>
        </Table>
      </Card>

      <TransactionDialog open={open} onClose={() => setOpen(false)} initialKind={(initialKind === "expense" || initialKind === "income" || initialKind === "reimbursement") ? initialKind : "expense"} />

      <ArchiveYearDialog
        open={archiveYearOpen}
        onClose={() => setArchiveYearOpen(false)}
        years={yearsList}
        onArchive={(y) => archiveYear.mutate(y)}
        onUnarchive={(y) => unarchiveYear.mutate(y)}
      />
    </>
  );
}

type TxRow = {
  id: string;
  date: string;
  kind: string;
  amount: number;
  description: string | null;
  archived_year?: boolean;
  receipt_url: string | null;
  external_source: string | null;
  external_id: string | null;
  treasury_categories?: { name?: string; accounting_code?: string | null } | null;
  activities?: { name?: string } | null;
  members?: { first_name: string; last_name: string } | null;
};

function TreasuryTxRow({
  t, sign, kindLabel, canAdmin, onDelete,
}: {
  t: TxRow;
  sign: "positive" | "negative" | "neutral";
  kindLabel: string;
  canAdmin: boolean;
  onDelete: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const canExpand = !!t.external_id && t.external_source === "helloasso";

  const { data: items, isLoading: itemsLoading } = useQuery({
    queryKey: ["helloasso-payment-items", t.external_id],
    enabled: open && !!t.external_id,
    queryFn: () => fetchHelloAssoPaymentItems(t.external_id as string),
    staleTime: 60_000,
  });

  return (
    <>
      <TableRow className={t.archived_year ? "opacity-60" : ""}>
        <TableCell className="p-1 text-center">
          {canExpand ? (
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              className="text-muted-foreground hover:text-foreground"
              aria-label={open ? "Replier" : "Voir le détail du panier"}
            >
              {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          ) : null}
        </TableCell>
        <TableCell>{t.date}{t.archived_year && <Badge variant="outline" className="ml-2 text-xs">archivé</Badge>}</TableCell>
        <TableCell><Badge variant="outline" className={SIGN_COLORS[sign]}>{kindLabel}</Badge></TableCell>
        <TableCell>{t.treasury_categories?.name} {t.treasury_categories?.accounting_code && <span className="text-xs text-muted-foreground">({t.treasury_categories.accounting_code})</span>}</TableCell>
        <TableCell className="text-muted-foreground">{t.activities?.name ?? "—"}</TableCell>
        <TableCell className="text-muted-foreground max-w-xs truncate">
          {t.description}
          {t.members && <span className="block text-xs">→ {t.members.first_name} {t.members.last_name}</span>}
        </TableCell>
        <TableCell>{t.receipt_url ? <a href={t.receipt_url} target="_blank" rel="noreferrer"><FileImage className="h-4 w-4 text-primary" /></a> : "—"}</TableCell>
        <TableCell className={`text-right font-mono ${t.kind === "income" ? "text-success" : "text-destructive"}`}>
          {t.kind === "income" ? "+" : "-"}{Number(t.amount).toFixed(2)} €
        </TableCell>
        <TableCell>{canAdmin && <Button variant="ghost" size="sm" onClick={() => onDelete(t.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>}</TableCell>
      </TableRow>
      {open && (
        <TableRow className="bg-muted/10">
          <TableCell colSpan={9} className="py-2">
            <TreasuryItemsDetail items={items} loading={itemsLoading} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function TreasuryItemsDetail({ items, loading }: { items: HelloAssoPaymentItem[] | undefined; loading: boolean }) {
  if (loading) {
    return <div className="flex items-center gap-2 text-xs text-muted-foreground px-4"><Loader2 className="h-3 w-3 animate-spin" /> Chargement du détail…</div>;
  }
  if (!items || items.length === 0) {
    return <div className="text-xs text-muted-foreground px-4">Aucun détail panier disponible. Utilisez « Reconstruire détail panier » dans Activités HelloAsso pour le générer.</div>;
  }
  return (
    <div className="px-4">
      <div className="text-xs font-medium text-muted-foreground mb-1">Détail du panier ({items.length} item{items.length > 1 ? "s" : ""})</div>
      <table className="w-full text-xs">
        <thead className="text-muted-foreground">
          <tr>
            <th className="text-left font-normal py-1">Item</th>
            <th className="text-left font-normal py-1">Type</th>
            <th className="text-right font-normal py-1">Qté</th>
            <th className="text-right font-normal py-1">Montant</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.id} className="border-t border-border/40">
              <td className="py-1">{it.name ?? "—"}</td>
              <td className="py-1 text-muted-foreground">{it.type ?? "—"}</td>
              <td className="py-1 text-right">{it.quantity}</td>
              <td className="py-1 text-right font-medium">{Number(it.amount).toFixed(2)} €</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ArchiveYearDialog({ open, onClose, years, onArchive, onUnarchive }: { open: boolean; onClose: () => void; years: string[]; onArchive: (y: string) => void; onUnarchive: (y: string) => void }) {
  const currentYear = new Date().getFullYear();
  const archivable = years.filter(y => Number(y) < currentYear);
  const [selected, setSelected] = useState<string>(archivable[0] ?? "");
  useEffect(() => { if (open && archivable.length && !selected) setSelected(archivable[0]); }, [open, archivable, selected]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Archiver / désarchiver une année</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Les mouvements archivés sont masqués par défaut de la liste. Ils restent consultables via le filtre « Inclure archives ». Recommandation : conserver au moins 6 années de données comptables.
          </p>
          <div className="space-y-1.5">
            <Label>Année</Label>
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger><SelectValue placeholder="Choisir une année…" /></SelectTrigger>
              <SelectContent>
                {archivable.length === 0 && <div className="px-2 py-4 text-sm text-muted-foreground">Aucune année antérieure à {currentYear}.</div>}
                {archivable.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose}>Fermer</Button>
          <Button variant="outline" disabled={!selected} onClick={() => { if (selected && confirm(`Désarchiver tous les mouvements de ${selected} ?`)) onUnarchive(selected); }}>
            Désarchiver
          </Button>
          <Button disabled={!selected} onClick={() => { if (selected && confirm(`Archiver tous les mouvements de ${selected} ?`)) onArchive(selected); }}>
            <Archive className="h-4 w-4 mr-2" />Archiver
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TransactionDialog({ open, onClose, initialKind = "expense" }: { open: boolean; onClose: () => void; initialKind?: "income" | "expense" | "reimbursement" }) {
  const qc = useQueryClient();
  const [kind, setKind] = useState<"income" | "expense" | "reimbursement">(initialKind);
  const [date, setDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [amount, setAmount] = useState("");
  const [categoryId, setCategoryId] = useState<string>("");
  const [activityId, setActivityId] = useState<string>("");
  const [description, setDescription] = useState("");
  const [memberId, setMemberId] = useState<string>("");
  const [reimbMethod, setReimbMethod] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const { data: cats = [] } = useQuery({
    queryKey: ["treasury_categories"],
    queryFn: async () => (await supabase.from("treasury_categories").select("*").eq("archived", false).order("name")).data ?? [],
  });
  const { data: members = [] } = useQuery({
    queryKey: ["members-list"],
    queryFn: async () => (await supabase.from("members").select("id,first_name,last_name").order("last_name")).data ?? [],
  });
  const { data: activities = [] } = useQuery({
    queryKey: ["activities"],
    queryFn: async () => (await supabase.from("activities").select("*").eq("archived", false).order("name")).data ?? [],
  });

  useEffect(() => {
    if (!open) return;
    setKind(initialKind);
    if (initialKind === "reimbursement") {
      supabase.from("treasury_categories")
        .select("id").eq("kind", "reimbursement").eq("archived", false)
        .order("name").limit(1)
        .then(({ data }) => { if (data && data[0]) setCategoryId(data[0].id); });
    } else {
      setCategoryId("");
    }
  }, [open, initialKind]);

  const filteredCats = cats.filter((c: any) => c.kind === kind);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setUploading(true);
    try {
      let receiptUrl: string | null = null;
      if (file) {
        const path = `${Date.now()}-${file.name.replace(/[^a-z0-9.-]/gi, "_")}`;
        const { error: upErr } = await supabase.storage.from("receipts").upload(path, file);
        if (upErr) throw upErr;
        const { data: signed } = await supabase.storage.from("receipts").createSignedUrl(path, 60 * 60 * 24 * 365 * 5);
        receiptUrl = signed?.signedUrl ?? null;
      }
      const { error } = await supabase.from("transactions").insert({
        kind, date, amount: Number(amount), category_id: categoryId || null,
        activity_id: activityId || null,
        description: description || null,
        member_id: kind === "reimbursement" ? (memberId || null) : null,
        reimbursement_method: kind === "reimbursement" ? (reimbMethod || null) : null,
        receipt_url: receiptUrl,
      });
      if (error) throw error;
      qc.invalidateQueries({ queryKey: ["transactions"] });
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
      qc.invalidateQueries({ queryKey: ["budget-actuals"] });
      toast.success("Mouvement enregistré");
      onClose();
      setAmount(""); setDescription(""); setFile(null); setReimbMethod(""); setMemberId(""); setCategoryId(""); setActivityId("");
    } catch (e: any) { toast.error(e.message); } finally { setUploading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{kind === "reimbursement" ? "Déposer une note de frais" : "Nouveau mouvement"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Type *</Label>
              <Select value={kind} onValueChange={(v: any) => { setKind(v); setCategoryId(""); }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="income">Recette</SelectItem>
                  <SelectItem value="expense">Dépense</SelectItem>
                  <SelectItem value="reimbursement">Remboursement de frais</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Date *</Label><Input type="date" required value={date} onChange={(e) => setDate(e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Montant (€) *</Label><Input type="number" step="0.01" required value={amount} onChange={(e) => setAmount(e.target.value)} /></div>
            <div className="space-y-1.5">
              <Label>Catégorie *</Label>
              <Select value={categoryId} onValueChange={setCategoryId}>
                <SelectTrigger><SelectValue placeholder="Choisir…" /></SelectTrigger>
                <SelectContent>
                  {filteredCats.map((c: any) => (<SelectItem key={c.id} value={c.id}>{c.name} {c.accounting_code && `(${c.accounting_code})`}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Activité concernée</Label>
            <Select value={activityId || "__none"} onValueChange={(v) => setActivityId(v === "__none" ? "" : v)}>
              <SelectTrigger><SelectValue placeholder="Aucune activité spécifique" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">Aucune activité spécifique</SelectItem>
                {activities.map((a: any) => (<SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Liste paramétrée par le trésorier dans l'onglet Activités.</p>
          </div>
          <div className="space-y-1.5"><Label>Nature de la dépense / description</Label><Textarea value={description} onChange={(e) => setDescription(e.target.value)} /></div>
          {kind === "reimbursement" && (
            <>
              <div className="space-y-1.5">
                <Label>Adhérent à rembourser</Label>
                <Select value={memberId} onValueChange={setMemberId}>
                  <SelectTrigger><SelectValue placeholder="Choisir…" /></SelectTrigger>
                  <SelectContent>
                    {members.map((m: any) => (<SelectItem key={m.id} value={m.id}>{m.first_name} {m.last_name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Mode de remboursement (commentaire)</Label>
                <Textarea placeholder="Ex : Virement le 15/03, chèque n°1234, espèces…" value={reimbMethod} onChange={(e) => setReimbMethod(e.target.value)} />
              </div>
            </>
          )}
          <div className="space-y-1.5">
            <Label>Justificatif (photo / PDF)</Label>
            <div className="flex items-center gap-2">
              <Input type="file" accept="image/*,application/pdf" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
              {file && <Upload className="h-4 w-4 text-muted-foreground" />}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
            <Button type="submit" disabled={uploading}>{uploading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Enregistrer</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// Onglet Types de mouvement
// ============================================================
function KindsTab() {
  const { hasAccess } = useAuth();
  const canAdmin = hasAccess("treasury", "admin");
  const qc = useQueryClient();
  const { data: kinds = [] } = useTransactionKinds();

  const [label, setLabel] = useState("");
  const [slug, setSlug] = useState("");
  const [sign, setSign] = useState<"positive" | "negative" | "neutral">("positive");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [eLabel, setELabel] = useState("");
  const [eSign, setESign] = useState<"positive" | "negative" | "neutral">("positive");

  const add = useMutation({
    mutationFn: async () => {
      const cleanSlug = (slug || label).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
      const max = kinds.reduce((m, k) => Math.max(m, k.sort_order), 0);
      const { error } = await supabase.from("transaction_kinds" as any).insert({
        slug: cleanSlug, label, sign, sort_order: max + 10,
      } as any);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["transaction_kinds"] }); setLabel(""); setSlug(""); setSign("positive"); toast.success("Type ajouté"); },
    onError: (e: any) => toast.error(e.message),
  });
  const update = useMutation({
    mutationFn: async () => {
      if (!editingId) return;
      const { error } = await supabase.from("transaction_kinds" as any).update({ label: eLabel, sign: eSign } as any).eq("id", editingId);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["transaction_kinds"] }); setEditingId(null); toast.success("Type modifié"); },
    onError: (e: any) => toast.error(e.message),
  });
  const toggleArchive = useMutation({
    mutationFn: async ({ id, archived }: { id: string; archived: boolean }) => {
      const { error } = await supabase.from("transaction_kinds" as any).update({ archived } as any).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["transaction_kinds"] }),
    onError: (e: any) => toast.error(e.message),
  });
  const del = useMutation({
    mutationFn: async (id: string) => { const { error } = await supabase.from("transaction_kinds" as any).delete().eq("id", id); if (error) throw error; },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["transaction_kinds"] }); toast.success("Supprimé"); },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card className="p-6 lg:col-span-2">
        <h3 className="font-medium mb-1">Types de mouvement</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Les types <strong>système</strong> (Recette, Dépense, Remboursement, Mouvement bancaire) sont câblés dans la logique métier (calcul du solde, conciliation, notes de frais) et ne peuvent pas être supprimés. Vous pouvez en créer d'autres pour catégoriser plus finement (ex. Subvention, Don) ; leur <em>signe</em> détermine s'ils s'ajoutent (+), se retranchent (−) ou restent neutres dans le solde.
        </p>
        <Table>
          <TableHeader><TableRow><TableHead>Libellé</TableHead><TableHead>Slug</TableHead><TableHead>Signe</TableHead><TableHead>Statut</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {kinds.map((k) => (
              <TableRow key={k.id}>
                {editingId === k.id ? (
                  <>
                    <TableCell><Input value={eLabel} onChange={(e) => setELabel(e.target.value)} className="h-8" /></TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{k.slug}</TableCell>
                    <TableCell>
                      <Select value={eSign} onValueChange={(v: any) => setESign(v)}>
                        <SelectTrigger className="h-8 w-32"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="positive">+ Recette</SelectItem>
                          <SelectItem value="negative">− Dépense</SelectItem>
                          <SelectItem value="neutral">Neutre</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>{k.is_system ? <Badge variant="outline">Système</Badge> : <Badge>Personnalisé</Badge>}</TableCell>
                    <TableCell className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => update.mutate()} disabled={!eLabel || update.isPending}><Check className="h-4 w-4 text-primary" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => setEditingId(null)}><X className="h-4 w-4" /></Button>
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell className={k.archived ? "text-muted-foreground line-through" : "font-medium"}>{k.label}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{k.slug}</TableCell>
                    <TableCell><Badge variant="outline" className={SIGN_COLORS[k.sign]}>{k.sign === "positive" ? "+" : k.sign === "negative" ? "−" : "="}</Badge></TableCell>
                    <TableCell>
                      {k.is_system ? <Badge variant="outline">Système</Badge> : k.archived ? <Badge variant="outline">Archivé</Badge> : <Badge>Actif</Badge>}
                    </TableCell>
                    <TableCell>
                      {canAdmin && (
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => { setEditingId(k.id); setELabel(k.label); setESign(k.sign); }}><Pencil className="h-4 w-4" /></Button>
                          {!k.is_system && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => toggleArchive.mutate({ id: k.id, archived: !k.archived })}>
                                {k.archived ? "Réactiver" : "Archiver"}
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => { if (confirm(`Supprimer le type "${k.label}" ? Les mouvements existants resteront mais perdront ce libellé.`)) del.mutate(k.id); }}>
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      {canAdmin && (
        <Card className="p-6">
          <h3 className="font-medium mb-4">Ajouter un type</h3>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Libellé *</Label><Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Ex : Subvention" /></div>
            <div className="space-y-1.5"><Label>Identifiant technique</Label><Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="Auto depuis le libellé" /></div>
            <div className="space-y-1.5">
              <Label>Signe comptable *</Label>
              <Select value={sign} onValueChange={(v: any) => setSign(v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="positive">+ Compte comme une recette</SelectItem>
                  <SelectItem value="negative">− Compte comme une dépense</SelectItem>
                  <SelectItem value="neutral">Neutre (ex. mouvement interne)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button className="w-full" onClick={() => add.mutate()} disabled={!label || add.isPending}>Ajouter</Button>
          </div>
        </Card>
      )}
    </div>
  );
}

function CategoriesTab() {
  const { hasAccess } = useAuth();
  const canAdmin = hasAccess("treasury", "admin");
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [kind, setKind] = useState<"income" | "expense" | "reimbursement">("expense");
  const [code, setCode] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editKind, setEditKind] = useState<"income" | "expense" | "reimbursement">("expense");
  const [editCode, setEditCode] = useState("");

  const { data: cats = [] } = useQuery({
    queryKey: ["treasury_categories"],
    queryFn: async () => (await supabase.from("treasury_categories").select("*").order("kind").order("name")).data ?? [],
  });

  const add = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("treasury_categories").insert({ name, kind, accounting_code: code || null });
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["treasury_categories"] }); setName(""); setCode(""); toast.success("Catégorie ajoutée"); },
    onError: (e: any) => toast.error(e.message),
  });
  const del = useMutation({
    mutationFn: async (id: string) => { const { error } = await supabase.from("treasury_categories").delete().eq("id", id); if (error) throw error; },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["treasury_categories"] }); toast.success("Supprimée"); },
    onError: (e: any) => toast.error(e.message),
  });
  const update = useMutation({
    mutationFn: async () => {
      if (!editingId) return;
      const { error } = await supabase.from("treasury_categories")
        .update({ name: editName, kind: editKind, accounting_code: editCode || null })
        .eq("id", editingId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["treasury_categories"] });
      setEditingId(null);
      toast.success("Catégorie modifiée");
    },
    onError: (e: any) => toast.error(e.message),
  });

  function startEdit(c: any) {
    setEditingId(c.id);
    setEditName(c.name);
    setEditKind(c.kind);
    setEditCode(c.accounting_code ?? "");
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card className="p-6 lg:col-span-2">
        <h3 className="font-medium mb-4">Catégories existantes</h3>
        <Table>
          <TableHeader><TableRow><TableHead>Nom</TableHead><TableHead>Type</TableHead><TableHead>Plan comptable</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {cats.map((c: any) => (
              <TableRow key={c.id}>
                {editingId === c.id ? (
                  <>
                    <TableCell><Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-8" /></TableCell>
                    <TableCell>
                      <Select value={editKind} onValueChange={(v: any) => setEditKind(v)}>
                        <SelectTrigger className="h-8 w-36"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="income">Recette</SelectItem>
                          <SelectItem value="expense">Dépense</SelectItem>
                          <SelectItem value="reimbursement">Remboursement</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell><Input value={editCode} onChange={(e) => setEditCode(e.target.value)} className="h-8 font-mono" /></TableCell>
                    <TableCell className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => update.mutate()} disabled={!editName || update.isPending}><Check className="h-4 w-4 text-primary" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => setEditingId(null)}><X className="h-4 w-4" /></Button>
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell>{c.name}</TableCell>
                    <TableCell><Badge variant="outline" className={SIGN_COLORS[c.kind === "income" ? "positive" : c.kind === "bank_movement" ? "neutral" : "negative"]}>{LEGACY_LABELS[c.kind] ?? c.kind}</Badge></TableCell>
                    <TableCell className="font-mono text-sm">{c.accounting_code ?? "—"}</TableCell>
                    <TableCell>
                      {canAdmin && (
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => startEdit(c)}><Pencil className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => { if (confirm("Supprimer ?")) del.mutate(c.id); }}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                        </div>
                      )}
                    </TableCell>
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      {canAdmin && (
        <Card className="p-6">
          <h3 className="font-medium mb-4">Ajouter une catégorie</h3>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Nom *</Label><Input value={name} onChange={(e) => setName(e.target.value)} /></div>
            <div className="space-y-1.5">
              <Label>Type *</Label>
              <Select value={kind} onValueChange={(v: any) => setKind(v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="income">Recette</SelectItem>
                  <SelectItem value="expense">Dépense</SelectItem>
                  <SelectItem value="reimbursement">Remboursement</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>N° plan comptable</Label><Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Ex : 606" /></div>
            <Button className="w-full" onClick={() => add.mutate()} disabled={!name || add.isPending}>Ajouter</Button>
          </div>
        </Card>
      )}
    </div>
  );
}

function MemberCategoriesTab() {
  const { hasAccess } = useAuth();
  const canAdmin = hasAccess("treasury", "admin");
  const qc = useQueryClient();

  const [label, setLabel] = useState("");
  const [fee, setFee] = useState("");
  const [isCopro, setIsCopro] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState("");
  const [editFee, setEditFee] = useState("");

  const { data: cats = [], isLoading } = useQuery({
    queryKey: [...memberCategoriesQueryKey, true],
    queryFn: () => fetchMemberCategories(true),
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: memberCategoriesQueryKey });
    qc.invalidateQueries({ queryKey: ["members"] });
  };

  const add = useMutation({
    mutationFn: async () => {
      const amount = fee === "" ? 0 : Number(fee);
      if (!Number.isFinite(amount) || amount < 0) throw new Error("Montant invalide");
      return api.post<MemberCategoryRow>("/api/member-categories", { label, fee: amount, is_copropriete: isCopro });
    },
    onSuccess: () => { refresh(); setLabel(""); setFee(""); setIsCopro(false); toast.success("Catégorie ajoutée"); },
    onError: (e: any) => toast.error(e.message),
  });

  const update = useMutation({
    mutationFn: async (patch: { id: string; label?: string; fee?: number; archived?: boolean }) => {
      const { id, ...rest } = patch;
      return api.patch<MemberCategoryRow>(`/api/member-categories/${encodeURIComponent(id)}`, rest);
    },
    onSuccess: () => { refresh(); setEditingId(null); toast.success("Catégorie mise à jour"); },
    onError: (e: any) => toast.error(e.message),
  });

  const del = useMutation({
    mutationFn: async (id: string) => api.delete(`/api/member-categories/${encodeURIComponent(id)}`),
    onSuccess: () => { refresh(); toast.success("Catégorie supprimée"); },
    onError: (e: any) => toast.error(e.message),
  });

  const startEdit = (c: MemberCategoryRow) => {
    setEditingId(c.id);
    setEditLabel(c.label);
    setEditFee(String(Number(c.fee ?? 0)));
  };

  const submitEdit = () => {
    if (!editingId) return;
    const amount = editFee === "" ? 0 : Number(editFee);
    if (!editLabel.trim()) { toast.error("Libellé requis"); return; }
    if (!Number.isFinite(amount) || amount < 0) { toast.error("Montant invalide"); return; }
    update.mutate({ id: editingId, label: editLabel.trim(), fee: amount });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card className="p-6 lg:col-span-2">
        <h3 className="font-medium mb-1">Catégories d'adhérents</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Le montant est proposé par défaut lors de la création d'un adhérent de cette catégorie.
        </p>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Libellé</TableHead>
            <TableHead className="text-right">Cotisation</TableHead>
            <TableHead>Adhérents</TableHead>
            <TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={4} className="text-center py-6 text-muted-foreground">Chargement…</TableCell></TableRow>
            ) : cats.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="text-center py-6 text-muted-foreground">Aucune catégorie.</TableCell></TableRow>
            ) : cats.map((c) => (
              <TableRow key={c.id} className={Number(c.archived) ? "opacity-60" : undefined}>
                {editingId === c.id ? (
                  <>
                    <TableCell><Input value={editLabel} onChange={(e) => setEditLabel(e.target.value)} className="h-8" /></TableCell>
                    <TableCell className="text-right">
                      <Input type="number" min="0" step="0.01" value={editFee} onChange={(e) => setEditFee(e.target.value)} className="h-8 w-24 ml-auto text-right" />
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{c.members_count ?? 0}</TableCell>
                    <TableCell className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={submitEdit} disabled={update.isPending}><Check className="h-4 w-4 text-primary" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => setEditingId(null)}><X className="h-4 w-4" /></Button>
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell>
                      <div className="font-medium">{c.label}</div>
                      <div className="flex gap-1 mt-1">
                        {Number(c.is_copropriete) ? <Badge variant="outline">Copropriété</Badge> : null}
                        {Number(c.is_system) ? <Badge variant="secondary">Système</Badge> : null}
                        {Number(c.archived) ? <Badge variant="outline">Archivée</Badge> : null}
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{Number(c.fee ?? 0).toFixed(2)} €</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{c.members_count ?? 0}</TableCell>
                    <TableCell>
                      {canAdmin && (
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => startEdit(c)} title="Modifier"><Pencil className="h-4 w-4" /></Button>
                          {Number(c.archived) ? (
                            <Button variant="ghost" size="sm" title="Réactiver" onClick={() => update.mutate({ id: c.id, archived: false })}>
                              <ArchiveRestore className="h-4 w-4 text-emerald-600" />
                            </Button>
                          ) : !Number(c.is_system) ? (
                            <Button variant="ghost" size="sm" title="Archiver" onClick={() => update.mutate({ id: c.id, archived: true })}>
                              <Archive className="h-4 w-4" />
                            </Button>
                          ) : null}
                          {!Number(c.is_system) && (
                            <Button variant="ghost" size="sm" title="Supprimer" onClick={() => { if (confirm(`Supprimer la catégorie « ${c.label} » ?`)) del.mutate(c.id); }}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {canAdmin && (
        <Card className="p-6">
          <h3 className="font-medium mb-4">Ajouter une catégorie</h3>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Libellé *</Label><Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Ex : Association" /></div>
            <div className="space-y-1.5"><Label>Montant de cotisation (€)</Label><Input type="number" min="0" step="0.01" value={fee} onChange={(e) => setFee(e.target.value)} placeholder="0" /></div>
            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <Label className="text-sm">Type copropriété</Label>
                <p className="text-xs text-muted-foreground">Demande le nombre de copropriétaires et permet le rattachement.</p>
              </div>
              <Switch checked={isCopro} onCheckedChange={setIsCopro} />
            </div>
            <Button className="w-full" onClick={() => add.mutate()} disabled={!label.trim() || add.isPending}>Ajouter</Button>
          </div>
        </Card>
      )}
    </div>
  );
}

function ActivitiesTab() {
  const { hasAccess } = useAuth();
  const canAdmin = hasAccess("treasury", "admin");
  const qc = useQueryClient();
  const [name, setName] = useState("");

  const { data: acts = [] } = useQuery({
    queryKey: ["activities-all"],
    queryFn: async () => (await supabase.from("activities").select("*").order("name")).data ?? [],
  });

  const add = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("activities").insert({ name });
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["activities-all"] }); qc.invalidateQueries({ queryKey: ["activities"] }); setName(""); toast.success("Activité ajoutée"); },
    onError: (e: any) => toast.error(e.message),
  });
  const toggle = useMutation({
    mutationFn: async ({ id, archived }: { id: string; archived: boolean }) => {
      const { error } = await supabase.from("activities").update({ archived }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["activities-all"] }); qc.invalidateQueries({ queryKey: ["activities"] }); },
    onError: (e: any) => toast.error(e.message),
  });
  const del = useMutation({
    mutationFn: async (id: string) => { const { error } = await supabase.from("activities").delete().eq("id", id); if (error) throw error; },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["activities-all"] }); qc.invalidateQueries({ queryKey: ["activities"] }); toast.success("Supprimée"); },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card className="p-6 lg:col-span-2">
        <h3 className="font-medium mb-4">Activités</h3>
        <p className="text-sm text-muted-foreground mb-3">Liste des activités proposées dans le formulaire de mouvement (ex : tournoi de printemps, gala, sortie raquettes…).</p>
        <Table>
          <TableHeader><TableRow><TableHead>Nom</TableHead><TableHead>Statut</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {acts.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-4">Aucune activité</TableCell></TableRow>}
            {acts.map((a: any) => (
              <TableRow key={a.id}>
                <TableCell className={a.archived ? "text-muted-foreground line-through" : ""}>{a.name}</TableCell>
                <TableCell>{a.archived ? <Badge variant="outline">Archivée</Badge> : <Badge>Active</Badge>}</TableCell>
                <TableCell className="text-right">
                  {canAdmin && (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => toggle.mutate({ id: a.id, archived: !a.archived })}>
                        {a.archived ? "Réactiver" : "Archiver"}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => { if (confirm("Supprimer ?")) del.mutate(a.id); }}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      {canAdmin && (
        <Card className="p-6">
          <h3 className="font-medium mb-4">Nouvelle activité</h3>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Nom *</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex : Tournoi printemps" /></div>
            <Button className="w-full" onClick={() => add.mutate()} disabled={!name || add.isPending}>Ajouter</Button>
          </div>
        </Card>
      )}
    </div>
  );
}

function BudgetTab() {
  const { hasAccess } = useAuth();
  const canAdmin = hasAccess("treasury", "admin");
  const qc = useQueryClient();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [groupBy, setGroupBy] = useState<"category" | "activity" | "crossed">("category");

  const [editOpen, setEditOpen] = useState(false);
  const [bCategoryId, setBCategoryId] = useState<string>("");
  const [bActivityId, setBActivityId] = useState<string>("");
  const [bAmount, setBAmount] = useState("");
  const [bNotes, setBNotes] = useState("");

  const { data: cats = [] } = useQuery({
    queryKey: ["treasury_categories"],
    queryFn: async () => (await supabase.from("treasury_categories").select("*").eq("archived", false).order("name")).data ?? [],
  });
  const { data: activities = [] } = useQuery({
    queryKey: ["activities"],
    queryFn: async () => (await supabase.from("activities").select("*").eq("archived", false).order("name")).data ?? [],
  });
  const { data: budgets = [] } = useQuery({
    queryKey: ["budgets", year],
    queryFn: async () => (await supabase.from("budgets").select("*, treasury_categories(name,kind), activities(name)").eq("year", year)).data ?? [],
  });
  const { data: actuals = [] } = useQuery({
    queryKey: ["budget-actuals", year],
    queryFn: async () => {
      const start = `${year}-01-01`;
      const end = `${year}-12-31`;
      const { data } = await supabase.from("transactions")
        .select("category_id,activity_id,amount,kind")
        .gte("date", start).lte("date", end)
        .neq("kind", "bank_movement");
      return data ?? [];
    },
  });

  const sumActual = (catId?: string | null, actId?: string | null) => {
    return actuals
      .filter((t: any) => (catId === undefined || t.category_id === catId) && (actId === undefined || t.activity_id === actId))
      .reduce((s: number, t: any) => s + Number(t.amount) * (t.kind === "income" ? 1 : -1), 0);
  };

  const addBudget = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("budgets").upsert({
        year,
        category_id: bCategoryId || null,
        activity_id: bActivityId || null,
        amount: Number(bAmount),
        notes: bNotes || null,
      }, { onConflict: "year,category_id,activity_id" });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["budgets", year] });
      setEditOpen(false); setBCategoryId(""); setBActivityId(""); setBAmount(""); setBNotes("");
      toast.success("Ligne budgétaire enregistrée");
    },
    onError: (e: any) => toast.error(e.message),
  });
  const delBudget = useMutation({
    mutationFn: async (id: string) => { const { error } = await supabase.from("budgets").delete().eq("id", id); if (error) throw error; },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["budgets", year] }); toast.success("Supprimée"); },
  });

  type Row = { key: string; label: string; sublabel?: string; planned: number; actual: number };
  let rows: Row[] = [];
  if (groupBy === "crossed") {
    rows = budgets.map((b: any) => ({
      key: b.id,
      label: b.treasury_categories?.name ?? "—",
      sublabel: b.activities?.name ?? "Toutes activités",
      planned: Number(b.amount),
      actual: sumActual(b.category_id ?? undefined, b.activity_id ?? undefined),
    }));
  } else if (groupBy === "category") {
    const map = new Map<string, Row>();
    for (const b of budgets as any[]) {
      const key = b.category_id ?? "none";
      const label = b.treasury_categories?.name ?? "Sans catégorie";
      const cur = map.get(key) ?? { key, label, planned: 0, actual: sumActual(b.category_id ?? undefined) };
      cur.planned += Number(b.amount);
      map.set(key, cur);
    }
    rows = Array.from(map.values());
  } else {
    const map = new Map<string, Row>();
    for (const b of budgets as any[]) {
      const key = b.activity_id ?? "none";
      const label = b.activities?.name ?? "Toutes activités";
      const cur = map.get(key) ?? { key, label, planned: 0, actual: sumActual(undefined, b.activity_id ?? undefined) };
      cur.planned += Number(b.amount);
      map.set(key, cur);
    }
    rows = Array.from(map.values());
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label>Année</Label>
          <Input type="number" className="w-28" value={year} onChange={(e) => setYear(Number(e.target.value) || currentYear)} />
        </div>
        <div className="space-y-1.5">
          <Label>Vue</Label>
          <Select value={groupBy} onValueChange={(v: any) => setGroupBy(v)}>
            <SelectTrigger className="w-[220px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="category">Par catégorie</SelectItem>
              <SelectItem value="activity">Par activité</SelectItem>
              <SelectItem value="crossed">Détaillée (catégorie × activité)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto">
          {canAdmin && <Button onClick={() => setEditOpen(true)}><Plus className="h-4 w-4 mr-2" />Ligne budgétaire</Button>}
        </div>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{groupBy === "activity" ? "Activité" : "Catégorie"}</TableHead>
              {groupBy === "crossed" && <TableHead>Activité</TableHead>}
              <TableHead className="text-right">Prévu (€)</TableHead>
              <TableHead className="text-right">Réalisé (€)</TableHead>
              <TableHead className="text-right">Écart</TableHead>
              <TableHead className="w-[160px]">Avancement</TableHead>
              {groupBy === "crossed" && canAdmin && <TableHead></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Aucune ligne budgétaire pour {year}. {canAdmin && "Créez-en une avec le bouton ci-dessus."}</TableCell></TableRow>
            )}
            {rows.map((r) => {
              const pct = r.planned > 0 ? Math.min(100, (Math.abs(r.actual) / r.planned) * 100) : 0;
              const over = r.planned > 0 && Math.abs(r.actual) > r.planned;
              const diff = r.planned - Math.abs(r.actual);
              return (
                <TableRow key={r.key}>
                  <TableCell className="font-medium">{r.label}</TableCell>
                  {groupBy === "crossed" && <TableCell className="text-muted-foreground">{r.sublabel}</TableCell>}
                  <TableCell className="text-right font-mono">{r.planned.toFixed(2)}</TableCell>
                  <TableCell className="text-right font-mono">{Math.abs(r.actual).toFixed(2)}</TableCell>
                  <TableCell className={`text-right font-mono ${diff < 0 ? "text-destructive" : "text-success"}`}>{diff.toFixed(2)}</TableCell>
                  <TableCell>
                    <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                      <div className={`h-full ${over ? "bg-destructive" : "bg-primary"}`} style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-xs text-muted-foreground">{pct.toFixed(0)}%</span>
                  </TableCell>
                  {groupBy === "crossed" && canAdmin && (
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => { if (confirm("Supprimer cette ligne budgétaire ?")) delBudget.mutate(r.key); }}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouvelle ligne budgétaire — {year}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Catégorie</Label>
              <Select value={bCategoryId || "__none"} onValueChange={(v) => setBCategoryId(v === "__none" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Toutes catégories" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">Toutes catégories</SelectItem>
                  {cats.map((c: any) => (<SelectItem key={c.id} value={c.id}>{c.name} ({LEGACY_LABELS[c.kind] ?? c.kind})</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Activité</Label>
              <Select value={bActivityId || "__none"} onValueChange={(v) => setBActivityId(v === "__none" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Toutes activités" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">Toutes activités</SelectItem>
                  {activities.map((a: any) => (<SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground">Au moins l'un des deux doit être renseigné pour suivre un poste précis.</p>
            <div className="space-y-1.5"><Label>Montant prévu (€) *</Label><Input type="number" step="0.01" value={bAmount} onChange={(e) => setBAmount(e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Notes</Label><Textarea value={bNotes} onChange={(e) => setBNotes(e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Annuler</Button>
            <Button onClick={() => addBudget.mutate()} disabled={!bAmount || addBudget.isPending}>
              {addBudget.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BankTab() {
  const { hasAccess } = useAuth();
  const canAdmin = hasAccess("treasury", "admin");
  const canWrite = hasAccess("treasury", "write");
  const qc = useQueryClient();
  const [accName, setAccName] = useState("");
  const [initBal, setInitBal] = useState("0");
  const [movOpen, setMovOpen] = useState(false);

  const { data: accounts = [] } = useQuery({
    queryKey: ["bank_accounts"],
    queryFn: async () => (await supabase.from("bank_accounts").select("*").eq("archived", false).order("name")).data ?? [],
  });
  const { data: movs = [] } = useQuery({
    queryKey: ["bank_movements"],
    queryFn: async () => (await supabase.from("transactions").select("*, bank_accounts(name)").eq("kind", "bank_movement").order("date", { ascending: false }).limit(100)).data ?? [],
  });

  const addAccount = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("bank_accounts").insert({ name: accName, initial_balance: Number(initBal) });
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["bank_accounts"] }); setAccName(""); setInitBal("0"); toast.success("Compte ajouté"); },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-6 lg:col-span-2">
          <h3 className="font-medium mb-4">Comptes bancaires</h3>
          <Table>
            <TableHeader><TableRow><TableHead>Nom</TableHead><TableHead className="text-right">Solde initial</TableHead></TableRow></TableHeader>
            <TableBody>
              {accounts.length === 0 && <TableRow><TableCell colSpan={2} className="text-center text-muted-foreground py-4">Aucun compte</TableCell></TableRow>}
              {accounts.map((a: any) => (
                <TableRow key={a.id}>
                  <TableCell className="font-medium">{a.name}</TableCell>
                  <TableCell className="text-right font-mono">{Number(a.initial_balance).toFixed(2)} €</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
        {canAdmin && (
          <Card className="p-6">
            <h3 className="font-medium mb-4">Ajouter un compte</h3>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Nom *</Label><Input value={accName} onChange={(e) => setAccName(e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Solde initial (€)</Label><Input type="number" step="0.01" value={initBal} onChange={(e) => setInitBal(e.target.value)} /></div>
              <Button className="w-full" onClick={() => addAccount.mutate()} disabled={!accName || addAccount.isPending}>Ajouter</Button>
            </div>
          </Card>
        )}
      </div>

      <Card>
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-medium">Mouvements bancaires</h3>
          {canWrite && accounts.length > 0 && <Button size="sm" onClick={() => setMovOpen(true)}><Plus className="h-4 w-4 mr-2" />Mouvement</Button>}
        </div>
        <Table>
          <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Compte</TableHead><TableHead>Description</TableHead><TableHead className="text-right">Montant</TableHead></TableRow></TableHeader>
          <TableBody>
            {movs.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-4">Aucun mouvement</TableCell></TableRow>}
            {movs.map((m: any) => (
              <TableRow key={m.id}>
                <TableCell>{m.date}</TableCell>
                <TableCell>{m.bank_accounts?.name ?? "—"}</TableCell>
                <TableCell className="text-muted-foreground">{m.description}</TableCell>
                <TableCell className="text-right font-mono">{Number(m.amount).toFixed(2)} €</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <BankMovementDialog open={movOpen} onClose={() => setMovOpen(false)} accounts={accounts} />
    </div>
  );
}

function BankMovementDialog({ open, onClose, accounts }: { open: boolean; onClose: () => void; accounts: any[] }) {
  const qc = useQueryClient();
  const [date, setDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [accountId, setAccountId] = useState("");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { error } = await supabase.from("transactions").insert({
      kind: "bank_movement", date, amount: Number(amount),
      bank_account_id: accountId || null, description: description || null,
    });
    if (error) { toast.error(error.message); return; }
    qc.invalidateQueries({ queryKey: ["bank_movements"] });
    toast.success("Mouvement enregistré");
    onClose(); setAmount(""); setDescription("");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Mouvement bancaire</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5"><Label>Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
          <div className="space-y-1.5">
            <Label>Compte</Label>
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger><SelectValue placeholder="Choisir…" /></SelectTrigger>
              <SelectContent>{accounts.map((a) => (<SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Montant (€)</Label><Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Description</Label><Textarea value={description} onChange={(e) => setDescription(e.target.value)} /></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
            <Button type="submit">Enregistrer</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// Onglet « Versements HelloAsso » (lecture seule)
// Agrège les paiements de helloasso_order_payments par id_cash_out.
// N'écrit RIEN dans la table transactions : aucun risque de double-comptage.
// ============================================================
type HAPayment = {
  id: string;
  amount: number;
  payment_date: string;
  cash_out_state: string | null;
  cash_out_date: string | null;
  id_cash_out: string | null;
  payer_first_name: string | null;
  payer_last_name: string | null;
  form_name: string | null;
  item_name: string | null;
};

type Payout = {
  cashId: string;
  cashDate: string;
  state: string;
  total: number;
  payments: HAPayment[];
};

function HelloAssoPayoutsTab() {
  const { hasAccess } = useAuth();
  const canAdmin = hasAccess("treasury", "admin");
  const qc = useQueryClient();
  const [syncing, setSyncing] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data: payments = [], isLoading } = useQuery({
    queryKey: ["helloasso_order_payments_all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("helloasso_order_payments" as any)
        .select("id,amount,payment_date,cash_out_state,cash_out_date,id_cash_out,payer_first_name,payer_last_name,form_name,item_name")
        .order("cash_out_date", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as HAPayment[];
    },
  });

  const DONE_STATES = ["Transfered", "CashedOut"];
  const isPendingState = (s: string | null) => !!s && !DONE_STATES.includes(s);

  const { payouts, pendingPayouts, pendingTotal } = useMemo(() => {
    const done = new Map<string, Payout>();
    const pending = new Map<string, Payout>();

    for (const p of payments) {
      const state = p.cash_out_state ?? "";
      const isDone = DONE_STATES.includes(state);
      const isPending = isPendingState(state);
      if (!isDone && !isPending) continue;

      // Clé : id_cash_out si présent, sinon clé synthétique par état (paiements pas encore regroupés)
      const key = p.id_cash_out || `__${state || "unknown"}__`;
      const target = isDone ? done : pending;
      const existing = target.get(key);
      const dateRef = p.cash_out_date ?? p.payment_date;

      if (existing) {
        existing.total += Number(p.amount) || 0;
        existing.payments.push(p);
        if (dateRef && dateRef > existing.cashDate) existing.cashDate = dateRef;
      } else {
        target.set(key, {
          cashId: p.id_cash_out || `(en attente · ${state || "—"})`,
          cashDate: dateRef,
          state,
          total: Number(p.amount) || 0,
          payments: [p],
        });
      }
    }

    const sortDesc = (a: Payout, b: Payout) => b.cashDate.localeCompare(a.cashDate);
    const pendingArr = Array.from(pending.values()).sort(sortDesc);
    return {
      payouts: Array.from(done.values()).sort(sortDesc),
      pendingPayouts: pendingArr,
      pendingTotal: pendingArr.reduce((s, p) => s + p.total, 0),
    };
  }, [payments]);

  const grandTotal = payouts.reduce((s, p) => s + p.total, 0);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await syncHelloAssoPayouts();
      toast.success(res.message ?? "Versements synchronisés");
      qc.invalidateQueries({ queryKey: ["helloasso_order_payments_all"] });
    } catch (e: any) {
      toast.error(e?.message ?? "Échec de la synchronisation");
    } finally {
      setSyncing(false);
    }
  };

  if (isLoading) {
    return <div className="flex justify-center p-8"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-semibold flex items-center gap-2"><Landmark className="h-4 w-4" />Versements bancaires HelloAsso</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Lecture seule — paiements regroupés par versement (<code>id_cash_out</code>). Indépendant des mouvements de trésorerie.
            </p>
          </div>
          {canAdmin && (
            <Button onClick={handleSync} disabled={syncing} size="sm">
              {syncing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
              Synchroniser
            </Button>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
          <div className="rounded-lg border p-3">
            <div className="text-xs text-muted-foreground">Versements</div>
            <div className="text-2xl font-semibold mt-0.5">{payouts.length}</div>
          </div>
          <div className="rounded-lg border p-3">
            <div className="text-xs text-muted-foreground">Total versé</div>
            <div className="text-2xl font-semibold mt-0.5 text-success">{grandTotal.toFixed(2)} €</div>
          </div>
          <div className="rounded-lg border p-3">
            <div className="text-xs text-muted-foreground">En attente de transfert</div>
            <div className="text-2xl font-semibold mt-0.5 text-warning">
              {pendingTotal.toFixed(2)} €
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {pendingPayouts.reduce((n, p) => n + p.payments.length, 0)} paiement(s)
            </div>
          </div>
        </div>
      </Card>

      {pendingPayouts.length > 0 && (
        <Card>
          <div className="px-4 py-3 border-b">
            <h4 className="font-medium text-sm flex items-center gap-2">
              <RefreshCw className="h-3.5 w-3.5" />
              Versements en cours de transfert
            </h4>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Dernier paiement</TableHead>
                <TableHead>Identifiant</TableHead>
                <TableHead>État</TableHead>
                <TableHead className="text-right">Paiements</TableHead>
                <TableHead className="text-right">Montant</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pendingPayouts.map((p) => (
                <>
                  <TableRow key={p.cashId}>
                    <TableCell>{format(new Date(p.cashDate), "dd/MM/yyyy")}</TableCell>
                    <TableCell className="font-mono text-xs">{p.cashId}</TableCell>
                    <TableCell><Badge variant="outline">{p.state || "—"}</Badge></TableCell>
                    <TableCell className="text-right">{p.payments.length}</TableCell>
                    <TableCell className="text-right font-semibold text-warning">{p.total.toFixed(2)} €</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => setExpanded(expanded === p.cashId ? null : p.cashId)}>
                        {expanded === p.cashId ? "Masquer" : "Détail"}
                      </Button>
                    </TableCell>
                  </TableRow>
                  {expanded === p.cashId && (
                    <TableRow key={p.cashId + "-detail"}>
                      <TableCell colSpan={6} className="bg-muted/30">
                        <div className="text-xs space-y-1 py-2">
                          {p.payments.map((pay) => (
                            <div key={pay.id} className="flex justify-between gap-3 border-b border-border/50 last:border-0 py-1">
                              <span className="text-muted-foreground">{format(new Date(pay.payment_date), "dd/MM/yyyy")}</span>
                              <span className="flex-1 truncate">
                                {[pay.payer_first_name, pay.payer_last_name].filter(Boolean).join(" ") || "—"}
                                {pay.form_name && <span className="text-muted-foreground"> · {pay.form_name}</span>}
                                {pay.item_name && <span className="text-muted-foreground"> · {pay.item_name}</span>}
                              </span>
                              <span className="font-medium tabular-nums">{Number(pay.amount).toFixed(2)} €</span>
                            </div>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {payouts.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground">
          Aucun versement HelloAsso synchronisé pour le moment.
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date versement</TableHead>
                <TableHead>Identifiant</TableHead>
                <TableHead>État</TableHead>
                <TableHead className="text-right">Paiements</TableHead>
                <TableHead className="text-right">Montant</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payouts.map((p) => (
                <>
                  <TableRow key={p.cashId}>
                    <TableCell>{format(new Date(p.cashDate), "dd/MM/yyyy")}</TableCell>
                    <TableCell className="font-mono text-xs">{p.cashId}</TableCell>
                    <TableCell><Badge variant="secondary">{p.state}</Badge></TableCell>
                    <TableCell className="text-right">{p.payments.length}</TableCell>
                    <TableCell className="text-right font-semibold text-success">{p.total.toFixed(2)} €</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => setExpanded(expanded === p.cashId ? null : p.cashId)}>
                        {expanded === p.cashId ? "Masquer" : "Détail"}
                      </Button>
                    </TableCell>
                  </TableRow>
                  {expanded === p.cashId && (
                    <TableRow key={p.cashId + "-detail"}>
                      <TableCell colSpan={6} className="bg-muted/30">
                        <div className="text-xs space-y-1 py-2">
                          {p.payments.map((pay) => (
                            <div key={pay.id} className="flex justify-between gap-3 border-b border-border/50 last:border-0 py-1">
                              <span className="text-muted-foreground">{format(new Date(pay.payment_date), "dd/MM/yyyy")}</span>
                              <span className="flex-1 truncate">
                                {[pay.payer_first_name, pay.payer_last_name].filter(Boolean).join(" ") || "—"}
                                {pay.form_name && <span className="text-muted-foreground"> · {pay.form_name}</span>}
                                {pay.item_name && <span className="text-muted-foreground"> · {pay.item_name}</span>}
                              </span>
                              <span className="font-medium tabular-nums">{Number(pay.amount).toFixed(2)} €</span>
                            </div>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
